import { useCallback, useEffect, useState } from "react";
import { useCarousel } from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import styles from "./SliderScrollBar.module.scss";
import { SliderControls } from "./SliderControls";

const useScrollBar = (emblaApi: ReturnType<typeof useCarousel>["emblaApi"]) => {
  const [value, setValue] = useState(0);

  const scrollToProgress = useCallback(
    (progress: number) => {
      if (!emblaApi) return;
      const { animation, limit, target, scrollProgress, scrollBody, scrollTo } =
        emblaApi.internalEngine();

      animation.stop();

      const currentProgress = scrollProgress.get(target.get());
      const allowedProgress = Math.min(Math.max(progress, 0), 1);
      const progressToTarget = allowedProgress - currentProgress;
      const distance = progressToTarget * limit.length * -1;

      scrollBody.useDuration(0);
      scrollTo.distance(distance, false);
    },
    [emblaApi],
  );

  const onScrollBarChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!emblaApi) return;
      const newProgress = parseFloat(event.target.value);
      setValue(newProgress);
      scrollToProgress(newProgress);
    },
    [emblaApi, scrollToProgress],
  );

  useEffect(() => {
    if (!emblaApi) return;

    setValue(emblaApi.scrollProgress());

    emblaApi.on("scroll", (api) => {
      setValue(api.scrollProgress());
    });
  }, [emblaApi]);

  return { value, onScrollBarChange };
};

type SliderScrollBarProps = {
  showControls?: boolean;
  className?: string;
};

export const SliderScrollBar = ({
  showControls,
  className,
}: SliderScrollBarProps) => {
  const { emblaApi } = useCarousel();
  const { value, onScrollBarChange } = useScrollBar(emblaApi);

  return (
    <div className={`${styles["scrollbar-wrapper"]} ${className ?? ""} `}>
      <input
        className={`${styles["scrollbar"]} is-box mt-5`}
        onChange={onScrollBarChange}
        type="range"
        min="0"
        max="1"
        step="0.001"
        value={value}
      />

      {showControls && <SliderControls className={styles["slider-controls"]} />}
    </div>
  );
};
